import * as zod from 'zod';

/**
 * Which streaming modality this External Control session expects. Determines the
 * control source the step acquires from arm-control-bot (and therefore which
 * external-control-bot path is authorized):
 *
 *  - pose            → pose streaming (EXTERNAL_CONTROL umbrella, or
 *                      COMPLIANCE_JOINT_TRAJECTORY when complianceMode is on).
 *  - joints          → joint-setpoint streaming on the dedicated `stream_joints`
 *                      topic (does NOT share /hardware/joint_trajectory with the
 *                      scaled_joint_trajectory_controller).
 *  - jointTrajectory → multi-waypoint trajectory via the
 *                      scaled_joint_trajectory_controller, unchanged.
 *
 * Either setpoint modality can route through the trajectory controller instead
 * under `onlinePlannerStreaming` — `joints` through ServoJ, `pose` through
 * ServoL — leasing `TRAJECTORY_CONTROLLER`. `jointTrajectory` never does.
 */
export const ExternalControlStreamType = zod.enum([
  'pose',
  'joints',
  'jointTrajectory',
]);
export type ExternalControlStreamType = zod.infer<
  typeof ExternalControlStreamType
>;

/**
 * Frame a streamed pose is expressed in (ServoL's `reference` slot). The wire
 * accepts a world-side parent only, so these are the two choices: `world`
 * additionally applies the configured `world_to_base` placement, so the two
 * differ by exactly that transform and otherwise coincide.
 */
export const ExternalControlReferenceFrame = zod.enum(['base', 'world']);
export type ExternalControlReferenceFrame = zod.infer<
  typeof ExternalControlReferenceFrame
>;

/**
 * Which subsystem an external client uses to take over:
 *  - api       → the client speaks the SDK/UDP protocol directly to
 *                external-control-bot (no bridge process).
 *  - rosHumble → external-control-bot spins up the ros-distro-bridge (Humble),
 *                which translates ROS topics into the SDK protocol.
 *  - rosJazzy  → same, for the Jazzy distro.
 *
 * All three (`api`, `rosHumble`, `rosJazzy`) are exposed in the step wizard and
 * the Streaming settings UI; external-control-bot maps each ROS distro to the
 * matching baked-in bridge conda env.
 */
export const ExternalControlControlledBy = zod.enum([
  'api',
  'rosHumble',
  'rosJazzy',
]);
export type ExternalControlControlledBy = zod.infer<
  typeof ExternalControlControlledBy
>;

// The REST models declare these fields as i32 and the TC reads them into a
// 32-bit int, so the API rejects anything above INT32_MAX instead of letting
// it overflow on the wire.
const INT32_MAX = 2_147_483_647;

const JOINT_COUNT = 6;

const JointLimitVector = zod
  .array(zod.number().finite().positive())
  .length(JOINT_COUNT, `must be ${JOINT_COUNT} values, one per joint`);

/** ServoL's optional per-joint caps; both vectors travel together, absent = no limit. */
export const ExternalControlMotionScalingLimits = zod.object({
  jointVelocityMax: JointLimitVector, // rad/s
  jointAccelerationMax: JointLimitVector, // rad/s²
});
export type ExternalControlMotionScalingLimits = zod.infer<
  typeof ExternalControlMotionScalingLimits
>;

/**
 * Optional trajectory-controller servo tuning, applied when the session streams
 * joints (ServoJ) or poses (ServoL) through the online planner. Any omitted
 * field falls back to the server default in `TcServoSession`.
 */
export const ExternalControlServoConfig = zod.object({
  // First-order low-pass time constant (τ), in milliseconds. The session
  // layer converts it to the TC's per-tick gain at enqueue:
  // k_p = 1 − exp(−dt/τ).
  tauMs: zod.number().int().min(1).max(1000).optional(),
  commandTimeoutMs: zod.number().int().positive().max(INT32_MAX).optional(),
  rampInDurationMs: zod.number().int().nonnegative().max(INT32_MAX).optional(),
  // Joints-only: the time constant (τ_c, ms) with which a streamed target's
  // difference from the position/velocity extrapolation decays, rather than
  // being applied in one control cycle. 0 = apply same-cycle. `message ServoL`
  // has no equivalent field.
  positionSmoothingTimeConstantMs: zod
    .number()
    .int()
    .min(0)
    .max(1000)
    .optional(),
  // Pose-only: ServoL's `reference` slot, and the axes its twist resolves
  // along.
  referenceFrame: ExternalControlReferenceFrame.optional(),
  // Pose-only per-joint caps for the ServoL motion; absent = no per-joint limit.
  motionScalingLimits: ExternalControlMotionScalingLimits.optional(),
});
export type ExternalControlServoConfig = zod.infer<
  typeof ExternalControlServoConfig
>;

/** Servo config with scalar defaults filled in; `motionScalingLimits` stays
 * optional (absent = no limit). */
export type ResolvedServoConfig = Required<
  Omit<ExternalControlServoConfig, 'motionScalingLimits'>
> &
  Pick<ExternalControlServoConfig, 'motionScalingLimits'>;

/**
 * Server-side servo defaults, applied per-field when `start()` omits them, and
 * shared by ServoJ and ServoL. `commandTimeoutMs` is sized for network jitter on
 * the client→ECB→TC path while staying far under ECB's 5 s writer eviction.
 * Also seeds the Streaming settings UI.
 */
export const DEFAULT_SERVO_CONFIG: ResolvedServoConfig = {
  tauMs: 20,
  commandTimeoutMs: 500,
  rampInDurationMs: 300,
  // ≈2× the common 100 Hz stream period, so a jitter lobe still pending when
  // its cancelling partner arrives mostly annihilates.
  positionSmoothingTimeConstantMs: 20,
  referenceFrame: 'base',
  // motionScalingLimits omitted: absent is its "no per-joint limit" default.
};

/**
 * Fills every omitted field with its server default. Both consumers of the
 * tuning go through here — the TC enqueue and the runner-state variables the
 * step publishes — so the values a client reads off the live session are
 * exactly the values the controller is running.
 */
export function resolveServoConfig(
  config: ExternalControlServoConfig | undefined,
): ResolvedServoConfig {
  return {
    tauMs: config?.tauMs ?? DEFAULT_SERVO_CONFIG.tauMs,
    commandTimeoutMs:
      config?.commandTimeoutMs ?? DEFAULT_SERVO_CONFIG.commandTimeoutMs,
    rampInDurationMs:
      config?.rampInDurationMs ?? DEFAULT_SERVO_CONFIG.rampInDurationMs,
    positionSmoothingTimeConstantMs:
      config?.positionSmoothingTimeConstantMs ??
      DEFAULT_SERVO_CONFIG.positionSmoothingTimeConstantMs,
    referenceFrame:
      config?.referenceFrame ?? DEFAULT_SERVO_CONFIG.referenceFrame,
    // No default to fill: absent stays absent (no per-joint limit).
    motionScalingLimits: config?.motionScalingLimits,
  };
}

/**
 * Which streamed quantity a TC-routed session drives, named as the `streamType`
 * the caller already holds rather than as the primitive it selects:
 * `joints` → ServoJ, `pose` → ServoL.
 */
export type TcServoModality = Exclude<
  ExternalControlStreamType,
  'jointTrajectory'
>;

/** The flags `resolveTcServoModality` reads, supplied by the caller so the rule
 * itself stays pure — botman reads them synchronously, the UI through hooks. */
export interface TcStreamingFlags {
  onlinePlannerStreaming: boolean;
  /** The TC master flag: without it the TC module and its connector don't exist. */
  useTrajectoryController: boolean;
  /** Selects external-control-bot's ZMQ backend, the only one with a
   * WriteServoJ / WriteServoL push. */
  zmqPoseMigration: boolean;
  /** The model's own Online Planning opt-in, resolved; always true on RO1/RO2. */
  onlinePlannerAllowedOnModel: boolean;
}

/** What `onlinePlannerStreaming` needs to actually take effect, in the order
 * they are checked — the first one missing is the one a fallback names. */
const TC_STREAMING_PREREQUISITES = [
  'useTrajectoryController',
  'zmqPoseMigration',
  'onlinePlannerAllowedOnModel',
] as const satisfies ReadonlyArray<keyof TcStreamingFlags>;

/**
 * The servo modality a session streams through the trajectory controller, or
 * null when it stays on the direct path.
 *
 * The single definition of that rule. botman decides the control-source lease
 * with it and the Streaming UI decides which controls to show with it — a
 * second copy on either side would offer tuning the robot ignores, or worse,
 * lease one path while the setpoints take the other.
 *
 * `jointTrajectory` never routes through the TC: it is a trajectory, not a
 * setpoint stream.
 */
export function resolveTcServoModality(
  streamType: ExternalControlStreamType,
  flags: TcStreamingFlags,
): TcServoModality | null {
  if (streamType === 'jointTrajectory' || !flags.onlinePlannerStreaming) {
    return null;
  }

  if (TC_STREAMING_PREREQUISITES.some((flag) => !flags[flag])) {
    return null;
  }

  return streamType;
}

/**
 * Why {@link resolveTcServoModality} kept a session on the direct path even
 * though `onlinePlannerStreaming` asked for the trajectory controller — or
 * null when there is nothing to say: the flag is off, or the stream type never
 * routes there. Reads the same prerequisite list as the rule, so a caller
 * logs the rule's own reason instead of re-deriving it.
 */
export function describeTcStreamingFallback(
  streamType: ExternalControlStreamType,
  flags: TcStreamingFlags,
): string | null {
  if (streamType === 'jointTrajectory' || !flags.onlinePlannerStreaming) {
    return null;
  }

  const missing = TC_STREAMING_PREREQUISITES.find((flag) => !flags[flag]);

  return missing
    ? `onlinePlannerStreaming is enabled but ${missing} is off; using the direct streaming path`
    : null;
}

export default zod.object({
  streamType: ExternalControlStreamType.default('pose'),
  controlledBy: ExternalControlControlledBy.default('api'),
  servoConfig: ExternalControlServoConfig.optional(),
});
